import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   * @param {boolean} [modal=true] - Whether to show as a modal.
   */
  showDialog(modal = true) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.isModal = modal;

    if (modal) {
      const scrollY = window.scrollY;
      this.#previousScrollY = scrollY;

      // Prevent layout thrashing by separating DOM reads from DOM writes
      requestAnimationFrame(() => {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;

        dialog.showModal();
        this.dispatchEvent(new DialogOpenEvent());

        document.documentElement.setAttribute('data-dialog-open', '');

        // Lock scroll if scroll-lock attribute is present
        if (dialog.hasAttribute('scroll-lock')) {
          document.documentElement.setAttribute('scroll-lock', '');
        }

        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      });
    } else {
      requestAnimationFrame(() => {
        dialog.show();
        this.dispatchEvent(new DialogOpenEvent());
        
        document.documentElement.setAttribute('data-dialog-open', '');
        
        // Lock scroll if scroll-lock attribute is present
        if (dialog.hasAttribute('scroll-lock')) {
          document.documentElement.setAttribute('scroll-lock', '');
        }
        
        document.addEventListener('click', this.#handleDocumentClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      });
    }
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    if (this.isModal) {
      this.removeEventListener('click', this.#handleClick);
    } else {
      document.removeEventListener('click', this.#handleDocumentClick);
    }
    
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (this.isModal) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
      
      requestAnimationFrame(() => {
        const hasOtherDialogsOpen = document.querySelector('dialog-component dialog[open]');
        if (!hasOtherDialogsOpen) {
          document.documentElement.removeAttribute('data-dialog-open');
        }
      });
    } else {
      const hasOtherDialogsOpen = document.querySelector('dialog-component dialog[open]');
      if (!hasOtherDialogsOpen) {
        document.documentElement.removeAttribute('data-dialog-open');
      }
    }

    // Unlock scroll if scroll-lock attribute is present
    if (dialog.hasAttribute('scroll-lock')) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Handles clicks on the document when the dialog is non-modal.
   * @param {MouseEvent} event - The mouse event.
   */
  #handleDocumentClick = (event) => {
    const { dialog } = this.refs;
    const target = /** @type {HTMLElement} */ (event.target);

    if (!dialog.contains(target) && !target.closest('[data-dialog-trigger]')) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   * Only closes if focus is outside the dialog content (same as click outside).
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    
    // Check if event was already handled (for Safari)
    if (event.defaultPrevented) return;

    const { dialog } = this.refs;
    const activeElement = document.activeElement;
    
    // Check if focus is INSIDE the dialog
    // If yes - do NOT close (allow user to work with content)
    if (activeElement && dialog.contains(activeElement)) {
      return;
    }
    
    // If focus is outside dialog (on backdrop or outside modal) - close it
    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
